import { useState, useCallback, useEffect } from "react";
import { Wand2, X, Loader2, ChevronRight, ChevronLeft, Sparkles, Check, Plus, Shirt, UserRound, BookOpen } from "lucide-react";

interface ImageFile {
  id: string;
  file: File;
  preview: string;
}

interface SmartPromptWizardProps {
  images: ImageFile[];
  onUsePrompt: (prompt: string) => void;
  onClose: () => void;
  savedPersonId?: number | null;
  savedGarmentId?: number | null;
  /** Roles already marked on the reference strip, used as the starting selection. */
  initialGarmentIds?: string[];
  initialTargetId?: string | null;
}

type LLMModel = "gpt-5-mini" | "gpt-5.2";

const TRANSFER_TYPES = [
  { id: "replace_top", label: "Zamijeni gornji dio", icon: "👕" },
  { id: "replace_bottom", label: "Zamijeni donji dio", icon: "👖" },
  { id: "replace_outfit", label: "Cijeli outfit", icon: "👗" },
  { id: "replace_dress", label: "Zamijeni haljinu", icon: "👘" },
  { id: "add_accessory", label: "Dodaj dodatak", icon: "👜" },
  { id: "style_match", label: "Uskladi stil", icon: "🎨" },
] as const;

const EXTRAS = [
  { id: "keep_pose", label: "Zadrži pozu" },
  { id: "keep_background", label: "Zadrži pozadinu" },
  { id: "photorealistic", label: "Fotorealistično" },
  { id: "studio_lighting", label: "Studijsko osvjetljenje" },
] as const;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function SmartPromptWizard({ images, onUsePrompt, onClose, savedPersonId, savedGarmentId, initialGarmentIds, initialTargetId }: SmartPromptWizardProps) {
  const [step, setStep] = useState(1);
  const [llmModel, setLlmModel] = useState<LLMModel>("gpt-5-mini");
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Setup
  const [transferType, setTransferType] = useState("");
  const [garmentImageIds, setGarmentImageIds] = useState<string[]>(
    initialGarmentIds?.length ? initialGarmentIds : images[0] ? [images[0].id] : []
  );
  const [targetImageId, setTargetImageId] = useState<string | null>(
    initialTargetId ?? (images.length >= 2 ? images[images.length - 1]?.id ?? null : null)
  );

  // Step 2: Analyze results
  const [garmentTags, setGarmentTags] = useState<string[]>([]);
  const [garmentDescription, setGarmentDescription] = useState("");
  const [personDescription, setCurrentOutfit] = useState("");
  const [analysisCost, setAnalysisCost] = useState<number | null>(null);
  const [newTag, setNewTag] = useState("");
  const [preAnalyzedGarment, setPreAnalyzedGarment] = useState(false);
  const [preAnalyzedPerson, setPreAnalyzedPerson] = useState(false);

  // Load saved garment analysis data if available
  useEffect(() => {
    if (savedGarmentId) {
      fetch(`/api/garments/${savedGarmentId}`)
        .then((r) => r.ok ? r.json() : null)
        .then((data) => {
          if (!data) return;
          // Auto-select transfer type from garment category
          if (data.category) setTransferType(data.category);
          // Pre-populate analysis if available
          if (data.analysisJson) {
            try {
              const analysis = JSON.parse(data.analysisJson);
              if (analysis.garmentTags) setGarmentTags(analysis.garmentTags);
              if (analysis.garmentDescription) setGarmentDescription(analysis.garmentDescription);
              setPreAnalyzedGarment(true);
            } catch { /* ignore */ }
          }
        })
        .catch(() => {});
    }
  }, [savedGarmentId]);

  // Load saved person analysis data if available
  useEffect(() => {
    if (savedPersonId) {
      fetch(`/api/persons/${savedPersonId}`)
        .then((r) => r.ok ? r.json() : null)
        .then((data) => {
          if (!data?.analysisJson) return;
          try {
            const analysis = JSON.parse(data.analysisJson);
            if (analysis.description) {
              setCurrentOutfit(analysis.description);
              setPreAnalyzedPerson(true);
            }
          } catch { /* ignore */ }
        })
        .catch(() => {});
    }
  }, [savedPersonId]);

  // Step 3: Extras
  const [extras, setExtras] = useState<string[]>([]);
  const [freeText, setFreeText] = useState("");

  // Step 4: Review
  const [composedPrompt, setComposedPrompt] = useState("");
  const [composing, setComposing] = useState(false);

  const handleAnalyze = useCallback(async () => {
    setAnalyzing(true);
    setError(null);
    // Clear pre-analyzed flags so fresh analysis can overwrite
    setPreAnalyzedGarment(false);
    setPreAnalyzedPerson(false);
    try {
      // Order images: all garment sources first, then target person, then any remaining
      const orderedImages: ImageFile[] = [];
      const garmentImgs = images.filter((img) => garmentImageIds.includes(img.id));
      const targetImg = images.find((img) => img.id === targetImageId);
      for (const img of garmentImgs) orderedImages.push(img);
      if (targetImg) orderedImages.push(targetImg);
      for (const img of images) {
        if (!orderedImages.some((o) => o.id === img.id)) orderedImages.push(img);
      }

      const base64Images = await Promise.all(orderedImages.map((img) => fileToBase64(img.file)));
      const res = await fetch("/api/smart-prompt/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          images: base64Images,
          model: llmModel,
          transferType,
          garmentCount: garmentImgs.length,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Analiza neuspješna");
      }
      const data = await res.json();
      const tags = data.garmentTags || [];
      const desc = data.garmentDescription || "";
      if (tags.length === 0 && !desc) {
        throw new Error("Analiza nije vratila rezultate. Pokušajte ponovo.");
      }
      // Only overwrite garment data if not pre-loaded from library
      if (!preAnalyzedGarment) {
        setGarmentTags(tags);
        setGarmentDescription(desc);
      }
      // Only overwrite person description if not pre-loaded from library
      const newPersonDesc = data.personDescription || "";
      if (!preAnalyzedPerson && newPersonDesc && newPersonDesc !== "N/A") {
        setCurrentOutfit(newPersonDesc);
      }
      setAnalysisCost(data.cost ?? null);
      setStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Analiza neuspješna");
    } finally {
      setAnalyzing(false);
    }
  }, [images, llmModel, garmentImageIds, targetImageId, transferType]);

  const handleCompose = useCallback(async () => {
    setComposing(true);
    setError(null);
    try {
      const res = await fetch("/api/smart-prompt/compose", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          garmentDescription,
          personDescription,
          transferType,
          extras,
          freeText,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Sastavljanje neuspješno");
      }
      const data = await res.json();
      setComposedPrompt(data.prompt);
      setStep(4);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sastavljanje neuspješno");
    } finally {
      setComposing(false);
    }
  }, [garmentDescription, personDescription, transferType, extras, freeText]);

  const removeTag = (index: number) => {
    setGarmentTags((prev) => prev.filter((_, i) => i !== index));
  };

  const addTag = () => {
    const tag = newTag.trim();
    if (tag && !garmentTags.includes(tag)) {
      setGarmentTags((prev) => [...prev, tag]);
      setNewTag("");
    }
  };

  const toggleExtra = (id: string) => {
    setExtras((prev) => prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]);
  };

  const STEP_LABELS = ["Postavi", "Analiza", "Dodatno", "Pregled"];
  const canAnalyze = transferType && garmentImageIds.length > 0;

  return (
    <div className="rounded-xl border border-primary/20 bg-primary/[0.03] p-3 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Wand2 className="w-3.5 h-3.5 text-primary" />
          <span className="text-[11px] font-medium text-primary">Smart Prompt</span>
          {analysisCost !== null && (
            <span className="text-[9px] text-muted-foreground/50 tabular-nums">
              ${analysisCost.toFixed(4)}
            </span>
          )}
        </div>
        <button onClick={onClose} className="text-muted-foreground/50 hover:text-muted-foreground transition-colors">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Step indicator */}
      <div className="flex gap-1">
        {STEP_LABELS.map((label, i) => {
          const stepNum = i + 1;
          const isActive = step === stepNum;
          const isDone = step > stepNum;
          return (
            <button
              key={label}
              onClick={() => isDone && setStep(stepNum)}
              disabled={!isDone}
              className={`flex-1 py-1 text-[9px] font-medium rounded-md transition-all ${
                isActive
                  ? "bg-primary/15 text-primary"
                  : isDone
                  ? "bg-primary/8 text-primary/60 cursor-pointer hover:bg-primary/12"
                  : "bg-secondary/30 text-muted-foreground/30"
              }`}
            >
              {isDone ? "✓ " : ""}{label}
            </button>
          );
        })}
      </div>

      {/* Error display */}
      {error && (
        <p className="text-[10px] text-destructive bg-destructive/10 px-2 py-1 rounded-md">{error}</p>
      )}

      {/* Step 1: Setup — garment type + image roles + model */}
      {step === 1 && (
        <div className="space-y-2.5">
          {/* Garment type picker */}
          <div className="space-y-1.5">
            <span className="text-[10px] text-muted-foreground/70">Šta prenosite?</span>
            <div className="grid grid-cols-3 gap-1">
              {TRANSFER_TYPES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTransferType(t.id)}
                  className={`flex items-center gap-1 px-1.5 py-1.5 text-[10px] font-medium rounded-md transition-all text-left ${
                    transferType === t.id
                      ? "bg-primary/12 text-primary ring-1 ring-primary/30"
                      : "bg-secondary/20 text-muted-foreground hover:bg-secondary/40"
                  }`}
                >
                  <span>{t.icon}</span>
                  <span className="truncate">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Image role assignment */}
          {images.length >= 2 && (
            <div className="space-y-1.5">
              <span className="text-[10px] text-muted-foreground/70">
                Dodijelite uloge slikama
                {garmentImageIds.length > 1 && (
                  <span className="ml-1 text-primary/70">({garmentImageIds.length} odjevnih predmeta odabrano)</span>
                )}
              </span>
              <div className="flex gap-1.5">
                {images.map((img) => {
                  const isGarment = garmentImageIds.includes(img.id);
                  const isTarget = targetImageId === img.id;
                  return (
                    <div key={img.id} className="flex flex-col items-center gap-1 flex-1 min-w-0">
                      <div
                        className={`relative w-full aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                          isGarment
                            ? "border-primary shadow-sm"
                            : isTarget
                            ? "border-emerald-500 shadow-sm"
                            : "border-border/40"
                        }`}
                      >
                        <img src={img.preview} alt="" className="w-full h-full object-cover" />
                        {isGarment && garmentImageIds.length > 1 && (
                          <span className="absolute top-0.5 right-0.5 bg-primary text-primary-foreground text-[8px] font-bold w-3.5 h-3.5 rounded-full flex items-center justify-center">
                            {garmentImageIds.indexOf(img.id) + 1}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-0.5 w-full">
                        <button
                          onClick={() => {
                            if (isGarment) {
                              // Deselect only if there's at least one other garment selected
                              if (garmentImageIds.length > 1) {
                                setGarmentImageIds((prev) => prev.filter((id) => id !== img.id));
                              }
                            } else {
                              // Add to garment selection
                              setGarmentImageIds((prev) => [...prev, img.id]);
                              if (targetImageId === img.id) setTargetImageId(null);
                            }
                          }}
                          className={`flex-1 flex items-center justify-center gap-0.5 py-1 text-[8px] font-medium rounded transition-all ${
                            isGarment
                              ? "bg-primary/15 text-primary"
                              : "bg-secondary/30 text-muted-foreground/50 hover:text-muted-foreground"
                          }`}
                          title="Izvorni odjevni predmet (višestruki odabir)"
                        >
                          <Shirt className="w-2.5 h-2.5" />
                          Odjeća
                        </button>
                        <button
                          onClick={() => {
                            setTargetImageId(img.id);
                            setGarmentImageIds((prev) => prev.filter((id) => id !== img.id));
                          }}
                          className={`flex-1 flex items-center justify-center gap-0.5 py-1 text-[8px] font-medium rounded transition-all ${
                            isTarget
                              ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                              : "bg-secondary/30 text-muted-foreground/50 hover:text-muted-foreground"
                          }`}
                          title="Ciljna osoba"
                        >
                          <UserRound className="w-2.5 h-2.5" />
                          Osoba
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Model selector */}
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground/70">AI Model</span>
            <div className="flex rounded-md bg-secondary/40 p-0.5 gap-0.5">
              {(["gpt-5-mini", "gpt-5.2"] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setLlmModel(m)}
                  className={`px-2 py-0.5 text-[10px] font-medium rounded transition-all ${
                    llmModel === m
                      ? "bg-card text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {m === "gpt-5-mini" ? "Brzo" : "Premium"}
                </button>
              ))}
            </div>
          </div>

          {/* Pre-analyzed badge + skip */}
          {(preAnalyzedGarment || preAnalyzedPerson) && (garmentTags.length > 0 || personDescription) && (
            <div className="flex items-center justify-between rounded-md bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1.5">
              <div className="flex items-center gap-1.5">
                <BookOpen className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400">Pre-analizirano iz biblioteke</span>
              </div>
              <button
                onClick={() => setStep(2)}
                disabled={!transferType}
                className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400 hover:underline disabled:opacity-30"
              >
                Preskoči na pregled →
              </button>
            </div>
          )}

          {/* Analyze button */}
          <button
            onClick={handleAnalyze}
            disabled={!canAnalyze || analyzing}
            className="w-full flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {analyzing ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Analiziranje odjeće...
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5" />
                {(preAnalyzedGarment || preAnalyzedPerson) ? "Ponovo analiziraj" : !transferType ? "Prvo odaberite vrstu odjeće" : `Analiziraj ${garmentImageIds.length} odjevn${garmentImageIds.length > 1 ? "ih predmeta" : "i predmet"}${targetImageId ? " + osoba" : ""}`}
              </>
            )}
          </button>
        </div>
      )}

      {/* Step 2: Analyze results */}
      {step === 2 && (
        <div className="space-y-2">
          <div className="space-y-1">
            <span className="text-[10px] text-muted-foreground/70">Oznake odjeće</span>
            <div className="flex flex-wrap gap-1">
              {garmentTags.map((tag, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-md"
                >
                  {tag}
                  <button onClick={() => removeTag(i)} className="text-primary/40 hover:text-primary">
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
              <div className="inline-flex items-center gap-0.5">
                <input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addTag()}
                  placeholder="Dodaj..."
                  className="w-14 text-[10px] bg-transparent border-none outline-none placeholder:text-muted-foreground/30"
                />
                {newTag.trim() && (
                  <button onClick={addTag} className="text-primary/60 hover:text-primary">
                    <Plus className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <span className="text-[10px] text-muted-foreground/70">Opis</span>
            <p className="text-[11px] text-foreground/80 leading-relaxed">{garmentDescription}</p>
          </div>

          {personDescription && personDescription !== "N/A" && (
            <div className="space-y-1">
              <span className="text-[10px] text-muted-foreground/70">Ciljna osoba</span>
              <p className="text-[11px] text-foreground/60 leading-relaxed">{personDescription}</p>
            </div>
          )}

          <div className="flex gap-1.5 pt-1">
            <button
              onClick={() => setStep(1)}
              className="flex items-center justify-center gap-1 px-3 py-1.5 text-[10px] font-medium rounded-md border border-border text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft className="w-3 h-3" /> Nazad
            </button>
            <button
              onClick={() => setStep(3)}
              className="flex-1 flex items-center justify-center gap-1 py-1.5 text-[10px] font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Dalje <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Extras */}
      {step === 3 && (
        <div className="space-y-2">
          <span className="text-[10px] text-muted-foreground/70">Opcije (opcionalno)</span>
          <div className="flex flex-wrap gap-1">
            {EXTRAS.map((e) => {
              const active = extras.includes(e.id);
              return (
                <button
                  key={e.id}
                  onClick={() => toggleExtra(e.id)}
                  className={`px-2 py-1 text-[10px] font-medium rounded-md transition-all ${
                    active
                      ? "bg-primary/12 text-primary ring-1 ring-primary/30"
                      : "bg-secondary/20 text-muted-foreground hover:bg-secondary/40"
                  }`}
                >
                  {active ? "✓ " : ""}{e.label}
                </button>
              );
            })}
          </div>
          <input
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder="Dodatne instrukcije..."
            className="w-full px-2 py-1.5 text-[11px] bg-secondary/20 border border-border/60 rounded-md outline-none focus:border-primary/40 placeholder:text-muted-foreground/30"
          />
          <div className="flex gap-1.5 pt-1">
            <button
              onClick={() => setStep(2)}
              className="flex items-center justify-center gap-1 px-3 py-1.5 text-[10px] font-medium rounded-md border border-border text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft className="w-3 h-3" /> Nazad
            </button>
            <button
              onClick={handleCompose}
              disabled={composing}
              className="flex-1 flex items-center justify-center gap-1 py-1.5 text-[10px] font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {composing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              Sastavi prompt
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Review */}
      {step === 4 && (
        <div className="space-y-2">
          <span className="text-[10px] text-muted-foreground/70">Generisani prompt</span>
          <div className="p-2 rounded-md bg-secondary/20 border border-border/40">
            <p className="text-[11px] leading-relaxed text-foreground/85">{composedPrompt}</p>
          </div>
          <div className="flex gap-1.5">
            <button
              onClick={() => setStep(3)}
              className="flex items-center justify-center gap-1 px-3 py-1.5 text-[10px] font-medium rounded-md border border-border text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft className="w-3 h-3" /> Uredi
            </button>
            <button
              onClick={() => { onUsePrompt(composedPrompt); onClose(); }}
              className="flex-1 flex items-center justify-center gap-1 py-2 text-[11px] font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <Check className="w-3.5 h-3.5" />
              Koristi ovaj prompt
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
